import type {
  WorkplaceRequestReservationFieldConfig,
  WorkplaceRequestTypeField
} from "@gettactic/api";

/**
 * Every switch off: what a `reservation` field allows before an admin turns
 * anything on, and how a field carrying no configuration at all is read.
 *
 * Core stores the three switches together and rejects a partial config, so
 * this is also the object the field builder seeds a new field with.
 */
export const EMPTY_RESERVATION_FIELD_CONFIG: WorkplaceRequestReservationFieldConfig =
  {
    recurrence_enabled: false,
    attendees_enabled: false,
    multi_day_enabled: false
  };

/**
 * Every switch on: what a `reservation` field is taken to collect when its
 * switches aren't known at all, so that nothing an answer already holds is
 * dropped for want of a config.
 *
 * This is the reading for a field derived from a request's *values* rather than
 * its type — a guest holding a share link never sees the type. Core keeps the
 * real rules either way, so assuming the most here can only be generous, while
 * assuming the least silently edits days, repeats and guests out of an answer.
 */
export const EVERY_RESERVATION_PART: WorkplaceRequestReservationFieldConfig = {
  recurrence_enabled: true,
  attendees_enabled: true,
  multi_day_enabled: true
};

/**
 * A reservation field's switches.
 *
 * `config` is optional on the wire, so reading it through here spares every
 * call site the "and then nothing is allowed" fallback. The returned object is
 * shared: patch it by spreading, never in place.
 *
 * ⚠️ Pass a field of the *type*. A field **value** read off a request carries
 * no `config` at all — core's `TicketFieldValue` has no such property — so this
 * would answer "nothing is allowed" for every one of them.
 */
export const reservationFieldConfig = (
  field: Pick<WorkplaceRequestTypeField, "config">
): WorkplaceRequestReservationFieldConfig =>
  field.config?.reservation ?? EMPTY_RESERVATION_FIELD_CONFIG;
